import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import CertificateHeader from './CertificateHeader';


/**
 * School Leaving Certificate - English Header Template
 * Matches reference image format with all 16 rows
 */
export const LCTemplateEnglish = ({ data }) => {
    const { school, student, details, issueDate } = data;

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
        const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
            return `${match[3]}/${match[2]}/${match[1]}`;
        }
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return dateStr;
            const d = String(date.getDate()).padStart(2, '0');
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const y = date.getFullYear();
            return `${d}/${m}/${y}`;
        } catch (e) {
            return dateStr;
        }
    };

    return (
        <Box sx={{
            fontFamily: '"Times New Roman", serif',
            fontSize: '12px',
            lineHeight: 1.4,
            color: '#000',
            backgroundColor: '#fff',
            p: 2,
            width: '100%',
            maxWidth: '210mm',
            minHeight: '297mm',
            boxSizing: 'border-box',
            margin: '0 auto',
            '@media print': {
                width: '210mm',
                maxWidth: '210mm',
                minHeight: '297mm',
                margin: 0,
                padding: '10mm',
            }
        }}>
            {/* School Header */}
            <CertificateHeader isLeavingCertificate={true} details={details} school={school} />


            {/* Registration Numbers Row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, fontSize: 11 }}>
                <Box>Sr. No. :- <strong>{details?.srNumber || '___'}</strong></Box>
                <Box>Index No:- <strong>{details?.indexNumber || '___'}</strong></Box>
                <Box>General Register No:- <strong>{details?.generalRegisterNo || '___'}</strong></Box>
            </Box>

            {/* U-DISE and Medium Row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, fontSize: 11 }}>
                <Box>School U-DISE Number – <strong>{school?.udiseNumber || '27250200605'}</strong></Box>
                <Box>Medium: <strong>{school?.medium || 'English'}</strong>, Board: <strong>{school?.board || 'SSC'}</strong></Box>
            </Box>

            {/* Certificate Title */}
            <Typography sx={{
                textAlign: 'center', fontSize: 18, fontWeight: 'bold',
                textDecoration: 'underline', mb: 2, fontStyle: 'italic'
            }}>
                School Leaving Certificate
            </Typography>

            {/* Student ID Row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, p: 1, border: '1px solid #000' }}>
                <Box>Student ID Number: <strong>{student?.admissionNumber}</strong></Box>
                <Box>Student UID (Aadhar Card): <strong>{details?.uid || '_____________'}</strong></Box>
            </Box>

            {/* Main Content Table */}
            <Box sx={{ border: '1px solid #000' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                        <TableRow num="1" label="Name of Student" value={`${student?.name || ''}`} />
                        <TableRow label="Father Name :" value={student?.fatherName || ''} indent />
                        <TableRow label="Mother Name :" value={student?.motherName || ''} indent />
                        <TableRow num="2" label="Nationality :" value={details?.nationality || 'Indian'} />
                        <TableRow num="3" label="Mother Tongue :" value={details?.motherTongue || 'Marathi'} />
                        <TableRow num="4" label="Religion :" value={details?.religion || ''} />
                        <TableRow num="5" label="Cast :" value={details?.caste || ''} />
                        <TableRow num="6" label="Sub Cast :" value={details?.subCaste || ''} />
                        <TableRow num="7" label="Place of Birth :" value={details?.placeOfBirth || ''} />
                        <TableRow label="Taluka :" value={details?.taluka || ''} indent />
                        <TableRow label="District:" value={details?.district || 'Pune'} indent />
                        <TableRow label="State:" value={details?.state || 'Maharashtra'} indent />
                        <TableRow label="Country:" value={details?.country || 'India'} indent />
                        <TableRow
                            num="8"
                            label="Date of Birth a)Figure (According to Christian)"
                            value={`${formatDate(details?.dateOfBirth)}, ${details?.dateOfBirthInWords || ''}`}
                        />
                        <TableRow label="b) In word" value={details?.dateOfBirthInWords || ''} indent />
                        <TableRow
                            num="9"
                            label="Last School Attended And Standard"
                            value={`${details?.lastSchool || ''}, ${details?.lastSchoolClass ? 'Std. ' + details?.lastSchoolClass : ''}`}
                        />
                        <TableRow
                            num="10"
                            label="Date of Admission With Class :"
                            value={`${formatDate(details?.dateOfAdmission)} ${details?.classAtAdmission ? 'Std. ' + details?.classAtAdmission : ''}`}
                        />
                        <TableRow num="11" label="Progress :" value={details?.progress || 'Good'} />
                        <TableRow num="12" label="Conduct :" value={details?.conduct || 'Good'} />
                        <TableRow num="13" label="Date of Leaving School :" value={formatDate(details?.dateOfLeaving)} />
                        <TableRow
                            num="14"
                            label="Std. In which Studying and Since When (In Figure)"
                            value={`Std. ${details?.currentClass || ''} ${details?.studyingSince || ''}`}
                        />
                        <TableRow num="15" label="Reason of Leaving School:" value={details?.reasonForLeaving || ''} />
                        <TableRow num="16" label="Remark :" value={details?.remarks || ''} />
                    </tbody>
                </table>
            </Box>

            {/* Disclaimer */}
            <Typography sx={{ fontSize: 10, mt: 1 }}>
                This above information is in accordance with the school Register.
            </Typography>

            {/* Signature Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 2 }}>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography>Date: {issueDate ? formatDate(issueDate) : formatDate(new Date())}</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ borderTop: '1px solid #000', width: 100, mb: 0.5 }} />
                    <Typography>Clerk</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ borderTop: '1px solid #000', width: 100, mb: 0.5 }} />
                    <Typography>Principal</Typography>
                </Box>
            </Box>

            {/* Footer */}
            <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #000', fontSize: 10 }}>
                <Typography sx={{ fontSize: 10 }}>
                    No change in any entry is to made except by authority issuing the
                    Leaving Certificate. Any infringement will be punished with rustication.
                </Typography>
            </Box>
        </Box>
    );
};

// Helper component for table rows
const TableRow = ({ num, label, value, indent }) => (
    <tr style={{ borderBottom: '1px solid #ddd' }}>
        <td style={{
            width: 30, padding: '4px 8px', borderRight: '1px solid #ddd',
            textAlign: 'center', fontSize: 11
        }}>
            {num || ''}
        </td>
        <td style={{
            width: '40%', padding: '4px 8px', borderRight: '1px solid #ddd',
            paddingLeft: indent ? 24 : 8, fontSize: 11
        }}>
            {label}
        </td>
        <td style={{ padding: '4px 8px', fontSize: 11, fontWeight: 'bold' }}>
            {value || '-'}
        </td>
    </tr>
);

export default LCTemplateEnglish;
